import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      const result = await login(username, password);

      if (result.success && result.user) {
        // user_type에 따라 리다이렉트
        if (result.user.user_type === 'admin') {
          // 관리자는 별도 로그인 페이지 사용
          setError('관리자는 관리자 로그인 페이지(/admin/login)를 사용해주세요.');
          await logout(); // 자동 로그아웃
          return;
        } else {
          // 일반 회원은 메인 페이지로
          await new Promise(resolve => setTimeout(resolve, 100));
          navigate('/');
        }
      } else {
        setError('아이디 또는 비밀번호가 일치하지 않습니다.');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError('로그인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className="flex-grow bg-white flex items-center justify-center px-4 sm:px-6 lg:px-8 py-16 sm:py-20 mt-16 sm:mt-20">
        <div className="max-w-md w-full">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              회원 로그인
            </h1>
            <p className="text-sm text-gray-600">
              동두천시 글로벌인재교육센터
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded p-8">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  아이디
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="아이디를 입력하세요"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  비밀번호
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="비밀번호를 입력하세요"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {error && (
                <div className="rounded bg-red-50 border border-red-200 p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? '로그인 중...' : '로그인'}
                </button>
              </div>

              <div className="text-center pt-5 mt-2 border-t border-gray-200 space-y-3">
                <p className="text-sm text-gray-600">
                  아직 회원이 아니신가요?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/signup')}
                    className="text-green-600 hover:text-green-700 font-medium hover:underline"
                  >
                    회원가입
                  </button>
                </p>
                <p className="text-sm text-gray-600">
                  아이디를 잊으셨나요?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/find-username')}
                    className="text-green-600 hover:text-green-700 font-medium hover:underline"
                  >
                    아이디 찾기
                  </button>
                </p>
                <p className="text-sm text-gray-600">
                  비밀번호를 잊으셨나요?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-green-600 hover:text-green-700 font-medium hover:underline"
                  >
                    비밀번호 찾기
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;